import { RASHI_NAMES } from '@astrally/shared';
import { useI18n } from '../../i18n';
import { arcPath } from './geometry';

/**
 * The East Indian (Bengali / Odia / Assamese) layout, annotated. Signs are
 * fixed like the South style, but the square's corner cells are split by the
 * diagonals that characterise the format. Cell arrangement mirrors the app's
 * real EastIndianChart exactly — the arrow follows the direction the signs
 * actually advance in that drawing — so this diagram teaches the chart the
 * reader will meet on their own chart screens, minus the chart data.
 */

const CELL = 75;

/** rashi index (0 = Mesha) → [col, row], same fixed map as EastIndianChart. */
const RASHI_CELL: [number, number][] = [
  [1, 0], // Mesha       top row
  [2, 0], // Vrishabha   top row
  [3, 0], // Mithuna     top-right corner
  [3, 1], // Karka       right column
  [3, 2], // Simha       right column
  [3, 3], // Kanya       bottom-right corner
  [2, 3], // Tula        bottom row
  [1, 3], // Vrischika   bottom row
  [0, 3], // Dhanu       bottom-left corner
  [0, 2], // Makara      left column
  [0, 1], // Kumbha      left column
  [0, 0], // Meena       top-left corner
];

const CORNERS = new Set(['0,0', '3,0', '0,3', '3,3']);

/** The corner diagonals all run toward the centre of the chart. */
function cornerDiagonal(col: number, row: number, x: number, y: number): string {
  if (col === 0 && row === 0) return `M ${x} ${y + CELL} L ${x + CELL} ${y}`;
  if (col === 3 && row === 0) return `M ${x} ${y} L ${x + CELL} ${y + CELL}`;
  if (col === 0 && row === 3) return `M ${x} ${y} L ${x + CELL} ${y + CELL}`;
  return `M ${x} ${y + CELL} L ${x + CELL} ${y}`;
}

export function EastLayout() {
  const { t } = useI18n();
  return (
    <svg
      viewBox="0 0 300 324"
      style={{ width: '100%', height: 'auto' }}
      role="img"
      aria-label={t('learn.fig.eastLayout.alt')}
    >
      <defs>
        <marker
          id="east-count"
          viewBox="0 0 8 8"
          refX="6"
          refY="4"
          markerWidth="7"
          markerHeight="7"
          orient="auto"
        >
          <path d="M 0 0 L 8 4 L 0 8 Z" fill="var(--mode-ink)" />
        </marker>
      </defs>

      <rect
        x="1"
        y="1"
        width="298"
        height="298"
        fill="none"
        stroke="var(--border-strong)"
        strokeWidth="1.5"
        rx="6"
      />

      {RASHI_NAMES.map((name, i) => {
        const [col, row] = RASHI_CELL[i];
        const x = col * CELL;
        const y = row * CELL;
        const isLagna = i === 0; // Mesha as the worked example
        const isCorner = CORNERS.has(`${col},${row}`);
        return (
          <g key={name}>
            <rect
              x={x}
              y={y}
              width={CELL}
              height={CELL}
              fill={isLagna ? 'var(--mode-soft)' : 'transparent'}
              stroke="var(--border-strong)"
              strokeWidth="1"
            />
            {isCorner && (
              <path
                d={cornerDiagonal(col, row, x, y)}
                stroke="var(--border)"
                strokeWidth="1"
                fill="none"
              />
            )}
            <text
              x={x + CELL / 2}
              y={y + CELL / 2 + (isLagna ? -2 : 3)}
              textAnchor="middle"
              fontSize="9.5"
              fill={isLagna ? 'var(--mode-ink)' : 'var(--text)'}
              fontWeight={isLagna ? 700 : 400}
            >
              {name}
            </text>
            {isLagna && (
              <text
                x={x + CELL / 2}
                y={y + CELL / 2 + 13}
                textAnchor="middle"
                fontSize="8.5"
                fill="var(--mode-ink)"
              >
                {t('learn.fig.lagna')}
              </text>
            )}
          </g>
        );
      })}

      {/* The count follows the fixed cells on from the lagna — in this
          arrangement Mesha's neighbour Vrishabha sits to its right. */}
      <path
        d={arcPath(150, 150, 44, -30, 60, 1)}
        fill="none"
        stroke="var(--mode-ink)"
        strokeWidth="1.5"
        markerEnd="url(#east-count)"
      />
      <text x={150} y={175} textAnchor="middle" fontSize="9" fill="var(--text-faint)">
        1 → 2 → 3
      </text>

      <text x={150} y={316} textAnchor="middle" fontSize="10" fill="var(--text-dim)">
        {t('learn.fig.eastLayout.note')}
      </text>
    </svg>
  );
}
